/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Responsive, ResponsiveProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import React, { memo } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { Theme } from '@mui/material';

import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    paddingTop: 8,
  },
  inner: {
    alignItems: 'center',
    display: 'flex',
    width: 200,
  },
  label: {
    color: theme.palette.text.secondary,
    flex: 1,
    fontSize: '0.8rem',
    minWidth: 80,
  },
  icon: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    outline: 'none',
    padding: '8px 0',
    '& > svg': {
      fill: theme.palette.text.secondary,
      flex: 1,
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)': {
      borderBottomRightRadius: 0,
      borderRightColor: 'transparent',
      borderTopRightRadius: 0,
    },
    '&:not(:first-of-type)': {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
      marginLeft: '-1px',
    },
  },
  selected: {
    backgroundColor: theme.palette.divider,
    '& > svg': {
      fill: theme.palette.text.disabled,
    },
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  name,
  onToggle,
  selected,
  children,
  disabled = false,
}: {
  name: string;
  onToggle: () => void;
  selected?: boolean;
  children: React.ReactNode;
  disabled: boolean;
}) {
  const classes = useStyles();

  return (
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onToggle)}
      role="button"
      tabIndex={0}
      onClick={onToggle}
      data-tip={`${selected ? 'Show' : 'Hide'} on ${name} devices`}
      className={clsx(
        classes.icon,
        selected ? classes.selected : null,
        disabled ? classes.disabled : null,
      )}
      aria-label={`${selected ? 'Show' : 'Hide'} on ${name} devices`}
    >
      {children}
    </div>
  );
}

function SVG({
  d,
  h,
  selected,
  w,
  'data-tip': dataTip,
}: {
  'data-tip'?: string;
  d: string;
  h: number;
  selected?: boolean;
  w: number;
}) {
  return (
    <svg
      style={{ pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      data-tip={dataTip}
    >
      <path d={d} />
      {selected ? (
        <line strokeWidth="2em" stroke="red" x1="0" y1="0" x2={w} y2={h} />
      ) : null}
    </svg>
  );
}

export function XS({
  selected,
  'data-tip': dataTip,
}: {
  selected?: boolean;
  'data-tip'?: string;
}) {
  return (
    <SVG
      data-tip={dataTip}
      d="M224 96v240H96V96h128m48-96H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM48 480c-8.8 0-16-7.2-16-16V48c0-8.8 7.2-16 16-16h224c8.8 0 16 7.2 16 16v416c0 8.8-7.2 16-16 16H48zM244 64H76c-6.6 0-12 5.4-12 12v280c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V76c0-6.6-5.4-12-12-12zm-48 352h-72c-6.6 0-12 5.4-12 12v8c0 6.6 5.4 12 12 12h72c6.6 0 12-5.4 12-12v-8c0-6.6-5.4-12-12-12z"
      h={512}
      selected={selected}
      w={320}
    />
  );
}

export function SM({
  selected,
  'data-tip': dataTip,
}: {
  selected?: boolean;
  'data-tip'?: string;
}) {
  return (
    <SVG
      data-tip={dataTip}
      d="M192 416c0 17.7-14.3 32-32 32s-32-14.3-32-32 14.3-32 32-32 32 14.3 32 32zM320 48v416c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48C0 21.5 21.5 0 48 0h224c26.5 0 48 21.5 48 48zm-32 0c0-8.8-7.2-16-16-16H48c-8.8 0-16 7.2-16 16v416c0 8.8 7.2 16 16 16h224c8.8 0 16-7.2 16-16V48z"
      h={512}
      selected={selected}
      w={320}
    />
  );
}

export function MD({
  selected,
  'data-tip': dataTip,
}: {
  selected?: boolean;
  'data-tip'?: string;
}) {
  return (
    <SVG
      data-tip={dataTip}
      d="M256 416c0 17.7-14.3 32-32 32s-32-14.3-32-32c0-21.3 14.3-32 32-32s32 14.3 32 32zM448 48v416c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V48C0 21.5 21.5 0 48 0h352c26.5 0 48 21.5 48 48zm-32 0c0-8.8-7.2-16-16-16H48c-8.8 0-16 7.2-16 16v416c0 8.8 7.2 16 16 16h352c8.8 0 16-7.2 16-16V48z"
      h={512}
      selected={selected}
      w={448}
    />
  );
}

export function LG({
  selected,
  'data-tip': dataTip,
}: {
  selected?: boolean;
  'data-tip'?: string;
}) {
  return (
    <SVG
      data-tip={dataTip}
      d="M624 368h-48V96c0-35.3-28.72-64-64-64H128c-35.28 0-64 28.7-64 64v272H16c-8.84 0-16 7.16-16 16v48c0 44.11 35.88 80 80 80h480c44.12 0 80-35.89 80-80v-48c0-8.84-7.16-16-16-16zM96 96c0-17.67 14.33-32 32-32h384c17.67 0 32 14.33 32 32v272H391.13c-4.06 0-7.02 3.13-7.92 7.09C379.98 389.35 367.23 400 352 400h-64c-15.23 0-27.98-10.65-31.21-24.91-.9-3.96-3.86-7.09-7.92-7.09H96V96zm512 336c0 26.47-21.53 48-48 48H80c-26.47 0-48-21.53-48-48v-32h194.75c6.59 18.62 24.38 32 45.25 32h96c20.88 0 38.66-13.38 45.25-32H608v32z"
      h={512}
      selected={selected}
      w={640}
    />
  );
}

export function XL({
  selected,
  'data-tip': dataTip,
}: {
  selected?: boolean;
  'data-tip'?: string;
}) {
  return (
    <SVG
      data-tip={dataTip}
      d="M528 0H48C21.5 0 0 21.5 0 48v288c0 26.5 21.5 48 48 48h192l-24 96h-72c-8.8 0-16 7.2-16 16s7.2 16 16 16h288c8.8 0 16-7.2 16-16s-7.2-16-16-16h-72l-24-96h192c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM249 480l16-64h46l16 64h-78zm295-144c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16v-48h512v48zm0-80H32V48c0-8.8 7.2-16 16-16h480c8.8 0 16 7.2 16 16v208z"
      h={512}
      selected={selected}
      w={576}
    />
  );
}
function ResponsiveProperty({
  appUid,
  activeItemId,
  mgr,
  pageId,
  property,
  value = getDefaultValues<Responsive>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<ResponsiveProp, Responsive>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  return (
    <div data-testid={property} className={classes.root}>
      <div data-tip={helpText} className={classes.label}>
        Responsive
      </div>
      <div className={classes.inner}>
        <Option
          disabled={isViewOnlyUser}
          name="Extra Large"
          onToggle={() => {
            onChange(property, { ...value, xl: !value.xl });
          }}
          selected={value.xl}
        >
          <XL selected={value.xl} />
        </Option>
        <Option
          disabled={isViewOnlyUser}
          name="Large"
          onToggle={() => {
            onChange(property, { ...value, lg: !value.lg });
          }}
          selected={value.lg}
        >
          <LG selected={value.lg} />
        </Option>
        <Option
          disabled={isViewOnlyUser}
          name="Medium"
          onToggle={() => {
            onChange(property, { ...value, md: !value.md });
          }}
          selected={value.md}
        >
          <MD selected={value.md} />
        </Option>
        <Option
          disabled={isViewOnlyUser}
          name="Small"
          onToggle={() => {
            onChange(property, { ...value, sm: !value.sm });
          }}
          selected={value.sm}
        >
          <SM selected={value.sm} />
        </Option>
        <Option
          disabled={isViewOnlyUser}
          name="Extra Small"
          onToggle={() => {
            onChange(property, { ...value, xs: !value.xs });
          }}
          selected={value.xs}
        >
          <XS selected={value.xs} />
        </Option>
      </div>
    </div>
  );
}

export default memo(ResponsiveProperty);
